import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorMessage } from "@/lib/api";
import { homeFor, useAuth } from "@/lib/auth";
import { LegalNotice } from "@/components/app/LegalNotice";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={homeFor(user)} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const u = await login(email, password);
      navigate(homeFor(u), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden lg:block">
        <img src="/img/login-signage.jpg" alt="Affichage numérique de menu" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/20" />
        <div className="absolute bottom-0 left-0 p-14">
          <p className="eyebrow text-cyan-400">Signalisation numérique · multitenant</p>
          <h2 className="mt-3 max-w-lg font-display text-4xl font-semibold leading-tight text-white">Vos écrans, vos menus, vos promos — pilotés depuis un seul portail.</h2>
          <p className="mt-4 max-w-md text-sm text-slate-300">Restaurants, commerces et hôtels : jumelez une TV Android en 30 secondes et planifiez chaque plage horaire.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="rise w-full max-w-sm" data-testid="login-form">
          <div className="mb-10">
            <img src="/img/logo-innova-sign.png" alt="Innova-Sign" data-testid="login-logo" className="h-16 w-auto" />
            <p className="mt-3 text-xs text-slate-500">Cloud · Portail d'administration</p>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-50">Connexion</h1>
          <p className="mt-2 text-sm text-slate-400">Accédez à la gestion de vos écrans.</p>
          <div className="mt-8 space-y-5">
            <div className="space-y-2"><Label htmlFor="email">Courriel</Label><Input id="email" type="email" autoComplete="email" required data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 border-slate-700 bg-slate-900/70" /></div>
            <div className="space-y-2"><div className="flex items-center justify-between"><Label htmlFor="password">Mot de passe</Label><Link to="/mot-de-passe-oublie" data-testid="link-forgot-password" className="text-xs text-blue-400 hover:text-blue-300">Mot de passe oublié ?</Link></div><Input id="password" type="password" autoComplete="current-password" required data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 border-slate-700 bg-slate-900/70" /></div>
            {error && <p data-testid="login-error" className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="login-submit-button" className="h-11 w-full bg-blue-600 text-white shadow-lg shadow-blue-500/20 hover:bg-blue-500">
              <Lock className="mr-2 h-4 w-4" />{busy ? "Connexion…" : "Se connecter"}
            </Button>
          </div>
          <p className="mt-8 text-xs text-slate-600">Les comptes sont créés par votre administrateur. Aucune inscription publique.</p>
          <Link to="/#telecharger" data-testid="login-download-apk" className="mt-3 inline-block text-xs text-orange-400 hover:text-orange-300">Télécharger l'application TV (APK)</Link>
          <LegalNotice className="mt-6" />
        </form>
      </div>
    </div>
  );
}
